import { S25Util } from "../../util/s25-util";
import { jSith } from "../../util/jquery-replacement";
import { FormatService } from "../../services/format.service";
import {
    AccountOccurrenceSubtotal,
    Invoice,
    InvoiceSummaryData,
    InvoiceSummarySubtotal,
    LineItem,
    LineItemAdjData,
    LineItemI,
    lineItemType,
    Occurrence,
    PricingOccurrence,
} from "../../pojo/Pricing";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { ListGeneratorService } from "../../services/list.generator.service";

export class S25PricingUtil {
    public static PricingConst = {
        cols: [
            { name: "Reference", prefname: "reference", sortable: 1, isDefaultVisible: 1 },
            { name: "Item", prefname: "item", sortable: 1, isDefaultVisible: 1 },
            { name: "List Price", prefname: "listPrice", sortable: 1, isDefaultVisible: 1, isMoney: 1 },
            { name: "Adjustments", prefname: "adjust", sortable: 1, isDefaultVisible: 1, templateType: 12 },
            { name: "Price", prefname: "price", sortable: 1, isDefaultVisible: 1, isMoney: 1 },
            { name: "Taxes", prefname: "tax", sortable: 1, isDefaultVisible: 1, templateType: 15 },
            { name: "Total", prefname: "total", sortable: 1, isDefaultVisible: 1, isMoney: 1 },
            { name: "Invoice To", prefname: "invoice", sortable: 1, isDefaultVisible: 1, isMoney: 1 },
            { name: "Charge To", prefname: "charge", sortable: 1, isDefaultVisible: 1, templateType: 13 },
            { name: "Rate Schedule", prefname: "schedule", sortable: 1, isDefaultVisible: 1 },
            { name: "Rate Group", prefname: "group", sortable: 1, isDefaultVisible: 1, templateType: 14 },
            { name: "Debit Account", prefname: "debit", sortable: 1, isDefaultVisible: 1 },
            { name: "Credit Account", prefname: "credit", sortable: 1, isDefaultVisible: 1 },
        ],
    };

    public static readonly StandardInvoiceName = "Standard Mode Invoice";
    public static readonly NonInvoicedOrgId = -99;
    public static readonly StandardPricingId = -999;
    public static readonly SummaryViewId = -998;

    public static convertToCents(dollars: number) {
        if (S25Util.isUndefined(dollars)) return 0;
        return Math.round(dollars * 100);
    }

    public static convertToDollars(cents: number) {
        if (S25Util.isUndefined(cents)) return 0;
        return +(cents / 100).toFixed(2);
    }

    public static billItemOrgFilter(orgId: any) {
        return function (billItem: any) {
            return parseInt(billItem.charge_to_id) === parseInt(orgId);
        };
    }

    public static getBillItemsByOrg(eventPricingData: any, orgId: any) {
        orgId = parseInt(orgId);
        return (S25Util.propertyGet(eventPricingData, "bill_item") || []).filter(
            S25PricingUtil.billItemOrgFilter(orgId),
        );
    }

    public static onlyItemLines(obj: any) {
        return parseInt(obj.bill_item_type_id) > 0;
    }

    public static onlySubtotalAdj(obj: any) {
        return parseInt(obj.bill_item_type_id) === -1 && parseInt(obj.ev_dt_profile_id) === -2;
    }

    public static itemAdjustmentPercToAmt(obj: any) {
        return (parseFloat(obj.adjustment_percent) || 0) * (parseFloat(obj.list_price) || 0);
    }

    private static formatExpandableFooterRows(billItems: LineItemI[]) {
        return billItems
            .filter(
                (item: LineItemI) =>
                    !!item.occurrences?.length &&
                    ((item.total_tax ?? 0) > 0 ||
                        (item.taxable_amt ?? item.price ?? item.list_price ?? 0) - (item.list_price ?? 0) !== 0),
            )
            .map((item: LineItemI) => ({
                itemName: item.bill_item_name,
                taxTotal: item.total_tax ?? 0,
                adjustmentAmt: (item.taxable_amt ?? item.price ?? item.list_price ?? 0) - (item.list_price ?? 0),
                eventId: item.eventId,
            }))
            .sort((a: LineItem, b: LineItem) =>
                a.itemName.localeCompare(b.itemName, undefined, { sensitivity: "base" }),
            );
    }

    public static agg: any = {
        listPrice: function (allBillItems: any) {
            return S25Util.array.sum(allBillItems.filter(S25PricingUtil.onlyItemLines), "list_price");
        },
        adjustments: {
            subtotal: function (allBillItems: any) {
                //how much was adjusted out of list price (eg, discount)
                return (
                    S25Util.array.sum(allBillItems.filter(S25PricingUtil.onlyItemLines), "adjustment_amt") +
                    S25Util.array.sum(
                        allBillItems.filter(S25PricingUtil.onlyItemLines).map(S25PricingUtil.itemAdjustmentPercToAmt),
                    )
                );
            },
            total: function (allBillItems: any) {
                return (
                    S25PricingUtil.agg.adjustments.subtotal(allBillItems) +
                    S25Util.array.sum(allBillItems.filter(S25PricingUtil.onlySubtotalAdj), "total_charge")
                );
            },
        },
        adjustedPrice: {
            subtotal: function (allBillItems: any) {
                return (
                    S25PricingUtil.agg.adjustments.subtotal(allBillItems) + S25PricingUtil.agg.listPrice(allBillItems)
                );
            },
            total: function (allBillItems: any) {
                return S25PricingUtil.agg.adjustments.total(allBillItems) + S25PricingUtil.agg.listPrice(allBillItems);
            },
        },
        taxes: {
            subtotal: function (allBillItems: any): number {
                return S25Util.array.sum(allBillItems.filter(S25PricingUtil.onlyItemLines), "total_tax");
            },
            total: function (allBillItems: any) {
                return (
                    S25PricingUtil.agg.taxes.subtotal(allBillItems) +
                    S25Util.array.sum(allBillItems.filter(S25PricingUtil.onlySubtotalAdj), "total_tax")
                );
            },
            totalByTaxId: function (allBillItems: any, taxesMap: any) {
                let taxIds = S25Util.propertyGetAllUnique(allBillItems, "tax_id");
                return taxIds.map(function (taxId: any) {
                    taxId = parseInt(taxId);
                    let taxName = taxesMap[taxId];
                    let taxBillItems: any[] = [];

                    jSith.forEach(allBillItems, function (_, billItem) {
                        jSith.forEach(billItem.tax, function (_, tax) {
                            if (parseInt(tax.tax_id) === taxId) {
                                let taxItem: any = {};
                                taxItem = S25Util.copy(taxItem, billItem);
                                taxName = tax.tax_name || taxName;
                                taxItem.total_tax = parseFloat(tax.tax_charge || 0);
                                taxBillItems.push(taxItem);
                            }
                        });
                    });

                    let totalTaxCharge = S25PricingUtil.agg.taxes.total(taxBillItems);
                    return {
                        tax_id: taxId,
                        tax_charge: totalTaxCharge,
                        itemValue: FormatService.toDollars(totalTaxCharge),
                        itemName: taxName,
                    };
                });
            },
        },
        totalCharge: {
            subtotal: function (allBillItems: any) {
                return (
                    S25PricingUtil.agg.adjustedPrice.subtotal(allBillItems) +
                    S25PricingUtil.agg.taxes.subtotal(allBillItems)
                );
            },
            total: function (allBillItems: any) {
                return (
                    S25PricingUtil.agg.adjustedPrice.total(allBillItems) + S25PricingUtil.agg.taxes.total(allBillItems)
                );
            },
        },
    };

    public static getSubTotalObject(org: any, billItems: any) {
        return {
            charge_to_name: org.organization_name,
            charge_to_id: org.organization_id,
            list_price: S25PricingUtil.agg.listPrice(billItems),
            adjustment_amt: S25PricingUtil.agg.adjustments.subtotal(billItems),
            taxable_amt: S25PricingUtil.agg.adjustedPrice.subtotal(billItems),
            total_tax: S25PricingUtil.agg.taxes.subtotal(billItems),
            total_charge: S25PricingUtil.agg.totalCharge.subtotal(billItems),
        };
    }

    //take event data, and other data, and form model used by s25-list
    public static getPricingOrganizationListModelData(
        eventPricingData: any,
        eventId: number,
        orgId: number,
        rateGroups: any,
        taxesMap: any,
        canEditPricing: boolean,
    ) {
        var eventData: any = {};
        eventData = S25Util.copy(eventData, eventPricingData); //copy of data used for puts
        delete eventData.orgToEvents; //remove orgToEvents from eventData since it is not needed for puts

        var allOrgs = S25Util.propertyGet(eventPricingData, "organization") || [];
        allOrgs = allOrgs.filter(function (org: any) {
            var orgId = parseInt(org.organization_id);
            return (
                !eventPricingData.orgToEvents ||
                (eventPricingData.orgToEvents[orgId] && eventPricingData.orgToEvents[orgId][eventId])
            );
        });

        var ret: any = {
            canEditPricing: canEditPricing,
            canEdit: true,
            canAdjust: true,
            orgId: orgId,
            eventId: eventId,
            evBillId: eventPricingData.evBillId,
            rateGroups: rateGroups,
            taxesMap: taxesMap,
            allOrgs: allOrgs,
            rows: [],
            allBillItems: [],
            eventData: eventData, //used for puts
        };

        jSith.forEach(S25PricingUtil.getBillItemsByOrg(eventPricingData, orgId), function (_, billItem) {
            S25Util.merge(billItem, {
                canEdit: ret.canEdit,
                canEditPricing: ret.canEditPricing,
                canAdjust: ret.canAdjust,
                eventId: billItem.eventId || ret.eventId,
                evBillId: billItem.evBillId || ret.evBillId,
                eventLocator: billItem.eventLocator || eventPricingData.event_locator,
            });

            if (billItem.bill_item_id > 0 && billItem.bill_item_type_id > 0) {
                ret.rows.push(billItem);
            }
            ret.allBillItems.push(billItem);
        });
        return ret;
    }

    //take model supplied by getPricingOrganizationListModelData and form general s25 styled list (headers, body, footer) to simulate a pricing list for a single org
    //(the output is a "getdata" function for a list)
    public static getPricingOrganizationListFn(orgModelData: any) {
        // var colsArray: any = [];
        // colsArray = S25Util.copy(colsArray, S25PricingUtil.PricingConst.cols);
        // !orgModelData.combineRelatedEvents && S25Util.array.inplaceRemoveByProp(colsArray, "prefname", "reference");
        var rowsF = function (listData: any) {
            if (orgModelData.hideNoChargeItems) {
                if (listData.isOccurrence) {
                    listData.reservations = listData.reservations.filter((row: LineItemI) => row.total);
                } else {
                    listData.rows = listData.rows.filter((row: LineItemI) => row.total_charge);
                }
            }
            //function ran by the list generator to create list rows from input data
            var ret: any[] = [];
            if (listData) {
                var eventIds = S25Util.propertyGetAllUnique(listData.allBillItems, "eventId") || [];
                jSith.forEach(listData.rows ?? listData.reservations, function (i, obj) {
                    //set data rows
                    var taxesObj: any = { tax: [], templateType: 15 };
                    let taxes = (!obj.occurrence && S25Util.propertyGet(obj, "tax")) || [];
                    jSith.forEach(taxes, function (_, tax) {
                        taxesObj.tax.push({
                            itemName: tax.tax_name || listData.taxesMap[tax.tax_id],
                            itemValue: FormatService.toDollars(tax.tax_charge),
                        });
                    });

                    var itemData = {
                        itemName:
                            (obj.bill_item_name || "") +
                            (obj.bill_profile_name && S25Util.profileName(obj.bill_profile_name.toString())
                                ? " (" + S25Util.profileName(obj.bill_profile_name.toString()) + ")"
                                : ""),
                        occurrences: obj.occurrences,
                        charge_to_id: obj.charge_to_id,
                        bill_item_id: obj.bill_item_id,
                        adjustment:
                            parseFloat(obj.adjustment_amt || 0) +
                            parseFloat(obj.adjustment_percent || 0) * (obj.taxable_amt || 0),
                        total_tax: obj.total_tax,
                        total_charge: (obj.total_charge ?? obj.total) || 0,
                        dateFormat: listData.dateFormat,
                        combineRelatedEvents: listData.combineRelatedEvents,
                    };
                    var row = [];
                    orgModelData.combineRelatedEvents && row.push(obj.eventLocator);
                    row = row.concat([
                        itemData,
                        (obj.list_price ?? obj.listPrice) || 0,
                        Object.assign({}, obj, {
                            eventData: listData.eventData ?? obj.eventData,
                            eventIds: eventIds,
                            combineRelatedEvents: listData.combineRelatedEvents,
                            adjustmentType: listData.rows ? 1 : 2, //type1 for line item adjustment, type2 for reservation adjustment
                            itemName: parseFloat(obj.adjustment_amt || 0) + parseFloat(obj.adjustment_percent || 0),
                            occView: !!listData.occView,
                            allOccurrences: listData.allOccurrences,
                            isOccurrence: listData.isOccurrence,
                            noOccItems: listData.noOccItems,
                            isNonInvoiced: listData.isNonInvoiced,
                        }), //itemName used for sorting
                        (obj.taxable_amt ?? obj.price) || 0,
                        taxesObj,
                        (obj.total_charge ?? obj.total) || 0,
                        Object.assign({ eventData: listData.eventData ?? obj.eventData }, obj, {
                            templateType: 13,
                            allOrgs: listData.allOrgs ?? obj.allOrgs,
                            isOccurrence: listData.isOccurrence,
                        }),
                        obj.rate_name,
                        Object.assign({ eventData: listData.eventData ?? obj.eventData }, obj, {
                            templateType: 14,
                            rateGroups: listData.rateGroups ?? obj.rateGroups,
                            itemName: obj.rate_group_name,
                            fls: listData.fls,
                            isOccurrence: listData.isOccurrence,
                        }),
                        obj.debit_account_number,
                        obj.credit_account_number,
                    ]);

                    listData.isSummaryView && row.splice(listData.combineRelatedEvents ? 7 : 6, 0, "");

                    ret.push({
                        itemName: itemData.itemName, //used for initial sort
                        isExpandedOcc: obj.isExpandedOcc ?? false,
                        row: row,
                    });
                });
            }
            ret.sort(S25Util.shallowSort("itemName")); //initial sort by bill item name
            if (ret.length === 0) {
                //at least one row (an empty one) so that list still shows since footer may have data
                ret.push({ row: ["(none)", "0", "", "0", "", "0", "", "", "", "", ""] });
            }
            return ret;
        };

        //list footer
        var footerF = function (listData: any): any {
            const isNonInvoiced = listData.orgId === S25PricingUtil.NonInvoicedOrgId;
            const occSubtotals = Object.values(listData?.occSubtotals ?? {});
            const taxTotal = S25PricingUtil.agg.taxes.subtotal(
                // if occView or non-invoiced data in summary view, only get total tax for non-occ items
                listData.occView ? listData.noOccItems : listData.allBillItems,
            );

            //same as rowsF but for the footer of the list (an optional component in general)
            var ret;
            if (listData && (listData.allBillItems?.length > 0 || (isNonInvoiced && listData.rows?.length > 0))) {
                const allBillItems = listData.allBillItems ?? listData.rows ?? [];
                var eventIds = S25Util.propertyGetAllUnique(allBillItems, "eventId") || [];
                //adjustments
                var adj = new Array(eventIds.length);
                for (var i = 0; i < adj.length; i++) {
                    var eventId = eventIds[i];
                    var eventBillItems = S25Util.propertyGetParentsWithChildValue(allBillItems, "eventId", eventId);
                    var eventLocator = eventBillItems && eventBillItems[0] && eventBillItems[0].eventLocator;
                    var adjRow = [];
                    orgModelData.combineRelatedEvents && adjRow.push(eventLocator);
                    adjRow = adjRow.concat([
                        `${listData.occView ? "Event " : ""}Adjustments`,
                        "",

                        //subtotal adjustments (plus add NEW adjustment)
                        {
                            allBillItems: eventBillItems,
                            eventIds: eventIds ?? eventId,
                            eventData: listData.eventData,
                            eventLocator: eventLocator,
                            occView: !!listData.occView,
                            noOccItems: listData.noOccItems,
                            profileSubtotals: listData.profileSubtotals,
                            canAdjust: listData.canAdjust,
                            canEdit: listData.canEdit,
                            canEditPricing: listData.canEditPricing,
                            eventId: eventId,
                            evBillId: listData.evBillId,
                            orgId: listData.orgId,
                            combineRelatedEvents: listData.combineRelatedEvents,
                        }, //can*, has* needed for perms while editing in the pricing modal

                        "",
                        "",

                        //sub total charges
                        eventBillItems,

                        "",
                        "",
                        "",
                        "",
                        "",
                    ]);
                    adj[i] = { row: adjRow, footerRow: true };
                }

                ret = [];

                //subtotals
                var subRow = [];
                orgModelData.combineRelatedEvents && subRow.push("");
                const listPrice =
                    S25PricingUtil.agg.listPrice(listData.occView ? listData.noOccItems : allBillItems) +
                    (listData.occView
                        ? occSubtotals.reduce(
                              (sum: number, item: AccountOccurrenceSubtotal) => sum + (item.occurrenceListPrice ?? 0),
                              0,
                          )
                        : 0);

                subRow = subRow.concat([
                    "Subtotal",
                    "" + (isNonInvoiced ? S25PricingUtil.getNonInvoicedListPrice(allBillItems) : listPrice), //sticker price
                    //list price is the sticker price and taxable amt is the before-tax price to the customer. The difference is thus the total adjustment
                    //note: we could filter bill_item_type_id > 0 but negative ones (totals/sub-total rows) dont have list_price or taxable_amt so we need not filter
                    FormatService.toDollars(
                        isNonInvoiced
                            ? S25PricingUtil.getNonInvoicedAdj(allBillItems)
                            : S25PricingUtil.agg.adjustments.subtotal(
                                  listData.occView ? listData.noOccItems : allBillItems,
                              ) +
                                  (listData.occView
                                      ? occSubtotals.reduce(
                                            (sum: number, item: AccountOccurrenceSubtotal) =>
                                                sum + (item.occurrenceAdjustments ?? 0),
                                            0,
                                        )
                                      : 0),
                    ), //adj col
                    "" +
                        (isNonInvoiced
                            ? S25PricingUtil.getNonInvoicedPrice(allBillItems)
                            : S25PricingUtil.agg.adjustedPrice.subtotal(
                                  listData.occView ? listData.noOccItems : allBillItems,
                              ) +
                              (listData.occView
                                  ? occSubtotals.reduce(
                                        (sum: number, item: AccountOccurrenceSubtotal) =>
                                            sum + (item.occurrenceTotalCharge ?? 0),
                                        0,
                                    )
                                  : 0)), //Price col
                    FormatService.toDollars(taxTotal), //Taxes col
                    "" +
                        (isNonInvoiced
                            ? S25PricingUtil.getNonInvoicedTotal(allBillItems)
                            : S25PricingUtil.agg.totalCharge.subtotal(
                                  listData.occView ? listData.noOccItems : allBillItems,
                              ) +
                              (listData.occView
                                  ? occSubtotals.reduce(
                                        (sum: number, item: AccountOccurrenceSubtotal) =>
                                            sum + (item.occurrenceTotalCharge ?? 0),
                                        0,
                                    )
                                  : 0)), //Total col,
                    "",
                    "",
                    "",
                    "",
                    "",
                ]);

                ret.push({ row: subRow, footerRow: true });
                if (listData.occView) {
                    const expandableData = S25PricingUtil.formatExpandableFooterRows(allBillItems),
                        lineItemAdj = S25PricingUtil.getOccLineItemAdjustments(allBillItems),
                        lineItemTaxes = S25PricingUtil.agg.taxes.subtotal(allBillItems) - taxTotal;
                    const lineItemRow = [
                        ...(listData.combineRelatedEvents ? [""] : []),
                        {
                            itemName: "Occurrence Line Item Amounts",
                            listItems: expandableData,
                            isExpandable: !!expandableData.length,
                            occLineItemAmounts: true,
                            charge_to_id: listData.orgId,
                        }, // subtotal row to account for line item adjustments for occurrence billing items
                        "",
                        S25PricingUtil.formatCurrency(lineItemAdj),
                        "",
                        S25PricingUtil.formatCurrency(S25PricingUtil.agg.taxes.subtotal(allBillItems) - taxTotal),
                        lineItemAdj + lineItemTaxes,
                        "",
                        "",
                        "",
                        "",
                        "",
                    ];

                    ret.push({ row: lineItemRow, footerRow: true });
                }

                //adjustments
                ret = ret.concat(adj);

                const totalAdjustments =
                    S25PricingUtil.agg.adjustments.total(allBillItems) +
                    (listData.occView
                        ? occSubtotals.reduce(
                              (sum: number, occ: AccountOccurrenceSubtotal) => sum + occ.occurrenceAdjustments,
                              0,
                          )
                        : 0);

                //totals
                var totalRow = [];
                const grandTotalTaxes = S25PricingUtil.agg.taxes.total(allBillItems); //tax total for all bill items, for use in line item view and non-invoiced data to reflect items associated with occurrences - occurrence view and summary view for invoiced items use "taxTotal" variable above
                orgModelData.combineRelatedEvents && totalRow.push("");
                totalRow = totalRow.concat([
                    "Total",
                    "" + (listData.orgId > 0 ? listPrice : S25PricingUtil.getNonInvoicedListPrice(allBillItems)), //sticker price
                    FormatService.toDollars(
                        listData.orgId > 0 ? totalAdjustments : S25PricingUtil.getNonInvoicedAdj(allBillItems),
                    ), //adj
                    "" +
                        (listData.orgId > 0
                            ? S25PricingUtil.agg.adjustedPrice.total(allBillItems)
                            : S25PricingUtil.getNonInvoicedPrice(allBillItems)), //price
                    FormatService.toDollars(grandTotalTaxes), //taxes
                    "" +
                        (listData.orgId > 0
                            ? S25PricingUtil.agg.totalCharge.total(allBillItems)
                            : S25PricingUtil.getNonInvoicedTotal(allBillItems) + (grandTotalTaxes - taxTotal)), //total
                    listData.paymentData ?? "",
                    "",
                    "",
                    "",
                    "",
                ]);
                ret.push({ row: totalRow, footerRow: true });
            } else {
                ret = S25PricingUtil.getSummaryFooterRows(listData.subtotals, listData.combineRelatedEvents);
            }

            return ret ?? [];
        };
        return {
            getRows: rowsF,
            getFooter: footerF,
        };
    }

    public static getSummaryFooterRows(orgSubtotals: any, combineRelatedEvents?: boolean) {
        let rows: any = [];
        const {
            eventTypeListPrice,
            eventTypeAdjustments,
            grossAdjustments,
            grandTotal,
            occurrenceListPrice,
            occurrenceAdjustments,
            outstandingBalance,
            requirementsListPrice,
            requirementsAdjustments,
            tax,
            occurrenceLineItems,
            nonOccurrenceLineItems,
        } = orgSubtotals;
        const eventTypePrice = eventTypeListPrice + eventTypeAdjustments,
            occurrencePrice = occurrenceListPrice + occurrenceAdjustments,
            requirementsPrice = requirementsListPrice + requirementsAdjustments,
            eventTypesInOccs = occurrenceLineItems?.filter((item: LineItem) => item.itemType === 1) ?? [],
            requirementsInOccs = occurrenceLineItems?.filter((item: LineItem) => item.itemType === 2) ?? [],
            locsResourcesNotInOccs =
                nonOccurrenceLineItems?.filter((item: LineItem) => item.itemType === 3 || item.itemType === 4) ?? [],
            eventTypesInOccsAdjustments =
                eventTypesInOccs?.reduce((sum: number, item: LineItem) => sum + (item.adjustmentAmt ?? 0), 0) ?? 0,
            locsResourcesNotInOccsAdjustments =
                locsResourcesNotInOccs?.reduce((sum: number, item: LineItem) => sum + (item.adjustmentAmt ?? 0), 0) ??
                0,
            requirementsNotInOccsAdjustments =
                requirementsInOccs?.reduce((sum: number, item: LineItem) => sum + (item.adjustmentAmt ?? 0), 0) ?? 0,
            orgListPrice =
                eventTypeListPrice -
                (eventTypesInOccs?.reduce((sum: number, item: LineItem) => sum + (item.listPrice ?? 0), 0) ?? 0) +
                occurrenceListPrice +
                (locsResourcesNotInOccs?.reduce((sum: number, item: LineItem) => sum + (item.listPrice ?? 0), 0) ?? 0) +
                requirementsListPrice -
                (requirementsInOccs?.reduce((sum: number, item: LineItem) => sum + (item.listPrice ?? 0), 0) ?? 0),
            orgSubtotalAdjustments =
                eventTypeAdjustments -
                eventTypesInOccsAdjustments +
                occurrenceAdjustments +
                locsResourcesNotInOccsAdjustments +
                requirementsAdjustments -
                requirementsNotInOccsAdjustments,
            netTotalAdjustments = grandTotal - tax - orgListPrice,
            combineEventsPlaceholder = combineRelatedEvents ? [""] : [];
        const taxableAmt =
            eventTypePrice -
            (eventTypesInOccs?.reduce((sum: number, item: LineItem) => sum + (item.price ?? 0), 0) ?? 0) +
            occurrencePrice +
            (locsResourcesNotInOccs?.reduce((sum: number, item: LineItem) => sum + (item.price ?? 0), 0) ?? 0) +
            requirementsPrice -
            (requirementsInOccs?.reduce((sum: number, item: LineItem) => sum + (item.price ?? 0), 0) ?? 0);
        const nonOccTax = nonOccurrenceLineItems.reduce((sum: number, item: LineItem) => sum + (item.tax ?? 0), 0),
            occTax = occurrenceLineItems.reduce((sum: number, item: LineItem) => sum + (item.tax ?? 0), 0);
        const subRow = [
            ...combineEventsPlaceholder,
            "Subtotal",
            orgListPrice,
            S25PricingUtil.formatCurrency(orgSubtotalAdjustments),
            taxableAmt,
            S25PricingUtil.formatCurrency(nonOccTax),
            taxableAmt + nonOccTax,
            ...Array(6).fill(""),
        ];
        rows.push({ row: subRow, footerRow: true });
        const expandableData = orgSubtotals.occurrenceLineItems
            .filter((item: LineItem) => item.tax > 0 || (item.price ?? 0) - (item.listPrice ?? 0) !== 0)
            .map((item: LineItem) => ({
                itemName: item.itemName,
                taxTotal: item.tax,
                adjustmentAmt: (item.price ?? 0) - (item.listPrice ?? 0),
                eventId: item.eventId,
            }))
            .sort((a: LineItem, b: LineItem) =>
                a.itemName.localeCompare(b.itemName, undefined, { sensitivity: "base" }),
            );
        const totalAdjustments = netTotalAdjustments - orgSubtotalAdjustments - grossAdjustments;
        const lineItemAdjRow = [
            ...combineEventsPlaceholder,
            {
                itemName: "Occurrence Line Item Amounts",
                listItems: expandableData,
                isExpandable: !!expandableData.length,
                occLineItemAmounts: true,
                charge_to_id: orgSubtotals.chargeToId,
            },
            "",
            S25PricingUtil.formatCurrency(totalAdjustments),
            "",
            S25PricingUtil.formatCurrency(occTax),
            totalAdjustments + occTax,
            ...Array(6).fill(""),
        ];
        rows.push({ row: lineItemAdjRow, footerRow: true });

        const eventAdjustments = [
            ...combineEventsPlaceholder,
            "Event Adjustments",
            "",
            S25PricingUtil.formatCurrency(grossAdjustments),
            "",
            "",
            grossAdjustments,
            ...Array(6).fill(""),
        ];
        rows.push({ row: eventAdjustments, footerRow: true });

        const total = [
            ...combineEventsPlaceholder,
            "Total",
            orgListPrice,
            S25PricingUtil.formatCurrency(netTotalAdjustments),
            grandTotal - tax,
            S25PricingUtil.formatCurrency(tax),
            grandTotal,
            "",
            { remainingBalance: outstandingBalance },
            ...Array(4).fill(""),
        ];
        rows.push({ row: total, footerRow: true });

        return rows;
    }

    public static formatOccDates(occs: Partial<Occurrence>[], occData: OccDataMap) {
        const occNames = occs.map((occ) => occ.occurrence);
        const noDupOccNames = new Set(occNames);

        occs.map((occ) => {
            if (
                occNames.length === noDupOccNames.size &&
                S25Datefilter.transform(occData[occ.rsrvId].rsrvStartDt, occData.dateFormat) ===
                    S25Datefilter.transform(occData[occ.rsrvId].rsrvEndDt, occData.dateFormat)
            ) {
                // all occurrence dates different - no need to specify times
                occ.occurrence = S25Datefilter.transform(
                    occ.occurrence ?? occData[occ.rsrvId].occurrence,
                    occData.dateFormat,
                );
            } else if (
                S25Datefilter.transform(occData[occ.rsrvId].rsrvStartDt, occData.dateFormat) !==
                S25Datefilter.transform(occData[occ.rsrvId].rsrvEndDt, occData.dateFormat)
            ) {
                // multi-day occurrences
                occ.occurrence = `${S25Datefilter.transform(
                    occData[occ.rsrvId].rsrvStartDt,
                    occData.dateFormat,
                )} - ${S25Datefilter.transform(occData[occ.rsrvId].rsrvEndDt, occData.dateFormat)}`;
            } else {
                // same day - different times occurrences
                occ.occurrence = `${S25Datefilter.transform(
                    occData[occ.rsrvId].rsrvStartDt,
                    occData.dateFormat,
                )} ${S25Datefilter.transform(
                    occData[occ.rsrvId].rsrvStartDt,
                    occData.timeFormat,
                )} - ${S25Datefilter.transform(occData[occ.rsrvId].rsrvEndDt, occData.timeFormat)}`;
            }

            return occ;
        });
    }
    //take model data supplied by caller and form the pricing totals list (the output is a "getdata" function for a list)
    public static getPricingTotalsListFn(modelData: any) {
        var colsArray = [
            { name: "Item", prefname: "item", sortable: 0, isDefaultVisible: 1 },
            { name: "List Price", prefname: "list_price", sortable: 0, isDefaultVisible: 1 },
            { name: "Adjustments", prefname: "adjustments", sortable: 0, isDefaultVisible: 1 },
            { name: "Price", prefname: "price", sortable: 0, isDefaultVisible: 1 },
            { name: "Taxes", prefname: "taxes", sortable: 0, isDefaultVisible: 1 },
            { name: "Total", prefname: "total", sortable: 0, isDefaultVisible: 1, isMoney: 1 },
            { name: "Charge To", prefname: "charge_to", sortable: 0, isDefaultVisible: 1 },
        ];
        var rowsF = function (listData: any) {
            var ret: any[] = [];
            if (listData) {
                jSith.forEach(listData.rows, function (_, obj) {
                    //set data rows
                    ret.push({
                        row: [
                            !obj.isAdjustment ? "Subtotals: " : "Adjustment: " + (obj.adjustment_name || ""),
                            !obj.isAdjustment ? FormatService.toDollars(obj.list_price) : "",
                            !obj.isAdjustment
                                ? FormatService.toDollars(obj.adjustment_amt)
                                : !isNaN(parseFloat(obj.adjustment_amt))
                                  ? FormatService.toDollars(obj.adjustment_amt)
                                  : !isNaN(parseFloat(obj.adjustment_percent))
                                    ? FormatService.formatPercent(obj.adjustment_percent)
                                    : "",
                            !obj.isAdjustment ? FormatService.toDollars(obj.taxable_amt) : "",
                            !obj.isAdjustment ? FormatService.toDollars(obj.total_tax) : "",
                            obj.total_charge || "0",
                            obj.charge_to_name,
                        ],
                    });
                });
            }
            ret.sort(S25Util.shallowSort("itemName"));
            return ret;
        };

        //list footer
        var footerF = function (listData: any) {
            var ret;
            if (listData && listData.rows.length > 0) {
                ret = [
                    //grand total
                    {
                        row: [
                            "Grand Total",
                            FormatService.toDollars(listData.grandListPrice),
                            FormatService.toDollars(listData.grandAdjustmentAmt),
                            FormatService.toDollars(listData.grandTaxableAmt || "0"),
                            S25Util.merge(listData.grandTaxes, {
                                templateType: 15,
                                evBillId: listData.evBillId,
                            }),
                            listData.grandTotalCharge || "0",
                            "",
                        ],
                    },
                ];
            }
            return ret;
        };
        return ListGeneratorService.s25Generate(
            null,
            colsArray,
            rowsF,
            function () {
                return jSith.when(modelData);
            },
            footerF,
        );
    }

    //take pricing data and form invoice model used for freshbooks integration, eg form "invoicesByEvent" json
    //@eventData: data from service call: PricingService.getPricingRelatedEvents
    //@orgId: org being billed for invoice
    public static formInvoiceModelFromPricing(eventData: any, orgId: number) {
        let invoicesByEvent: any = {
            events: [],
            totalOrgCharge: 0,
        };

        //sort events by start dt
        eventData && eventData.sort(S25Util.shallowSort("start_date"));

        //populate events and compute overall total charge (for this org)
        jSith.forEach(eventData, function (_, e) {
            //for each event
            let orgBillingItems = S25PricingUtil.getBillItemsByOrg(e, orgId);
            let orgTotalCharge = S25PricingUtil.agg.totalCharge.total(orgBillingItems);
            if (orgTotalCharge !== 0) {
                invoicesByEvent.totalOrgCharge += orgTotalCharge; //keep track of total charge across events in org

                //form indiv event info with org billing info
                let event: any = {
                    eventName: e.event_name,
                    eventLocator: e.event_locator,
                    eventId: e.event_id,
                    eventStartDt: e.start_date,
                    eventEndDt: e.end_date,
                    profile: [],
                    requirements: [],
                    orgAdjustments: [],
                };

                //sort profiles by init_start_dt
                e.profile = e.profile || [];
                e.profile.sort(S25Util.shallowSort("init_start_dt"));

                //separate billing items into profiles on the event
                //these line items include any line item adjustments as well
                jSith.forEach(e.profile as any[], function (i, p) {
                    let orgProfileBilling = orgBillingItems.filter(function (obj: any) {
                        return parseInt(obj.ev_dt_profile_id) === parseInt(p.profile_id);
                    });
                    orgProfileBilling.sort(S25Util.shallowSort("bill_item_name"));
                    if (orgProfileBilling.length) {
                        event.profile.push({
                            profileName: S25Util.coalesce(
                                S25Util.profileName(p.profile_name.toString()),
                                "Segment " + (i + 1),
                            ),
                            billingItems: orgProfileBilling,
                        });
                    }
                });

                //requirement items on event (exist outside any event profile)
                event.requirements = orgBillingItems
                    .filter(function (billItem: any) {
                        var profileId = parseInt(billItem.ev_dt_profile_id);
                        var totalCharge = parseFloat(billItem.total_charge);
                        var billItemTypeId = parseInt(billItem.bill_item_type_id);
                        var listPrice = parseFloat(billItem.list_price);

                        return (
                            profileId === 0 &&
                            billItemTypeId === 2 &&
                            !isNaN(totalCharge) &&
                            !isNaN(listPrice) &&
                            listPrice !== 0
                        );
                    })
                    .sort(S25Util.shallowSort("bill_item_name"));

                //adjustments on event (exist outside any event profile)
                event.orgAdjustments = orgBillingItems
                    .filter(function (billItem: any) {
                        var profileId = parseInt(billItem.ev_dt_profile_id);
                        var totalCharge = parseFloat(billItem.total_charge);
                        var billItemTypeId = parseInt(billItem.bill_item_type_id);

                        return profileId === -2 && billItemTypeId === -1 && !isNaN(totalCharge);
                    })
                    .sort(S25Util.shallowSort("bill_item_type_name")); //these are sorted by bill item TYPE name

                invoicesByEvent.events.push(event); //append to model array of events
            }
        });

        return invoicesByEvent;
    }

    public static formatCurrency(data: number, format: string = "USD") {
        const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: format.toUpperCase(),
        });

        return formatter.format(data);
    }

    public static getAdjustmentRows(model: any, lineItem: any, adjustments: any, subtotals: any, eventLocatorMap: any) {
        const { canAdjust, canEdit, canEditPricing, evBillId } = model;

        const adjustmentsMap: any = {};

        if (adjustments) {
            adjustments.map((adjustment: any) => {
                const adjustmentItem = {
                    ...model,
                    adjustment_amt: adjustment.adjustmentAmt,
                    adjustment_percent: adjustment.adjustmentPercent / 100,
                    adjustment_name: adjustment.adjustmentName,
                    charge_to_id: adjustment.chargeToId,
                    charge_to_name: model.charge_to_name,
                    bill_item_id: adjustment.itemId,
                    bill_item_type_id: -1,
                    total_charge: adjustment.totalCharge,
                    isAdjustment: true,
                };
                if (!adjustmentsMap[adjustment.chargeToId]) {
                    adjustmentsMap[adjustment.chargeToId] = [adjustmentItem];
                } else {
                    adjustmentsMap[adjustment.chargeToId].push(adjustmentItem);
                }
            });
        } else {
            adjustmentsMap[lineItem.chargeToId] = model.eventData.bill_item.filter((billItem: any) => {
                billItem.canAdjust = canAdjust;
                billItem.canEdit = canEdit;
                billItem.canEditPricing = canEditPricing;
                billItem.evBillId = evBillId;

                return eventLocatorMap[billItem.eventId];
            });
        }
        let data: any = [];
        const occLineItemAdj = S25PricingUtil.getOccLineItemAdjustments(model.eventData.bill_item);
        const occLineItemTaxes =
            model.eventData.bill_item.reduce((sum: number, item: LineItemI) => {
                if (item.occurrences?.length) sum += item.total_tax ?? 0;
                return sum;
            }, 0) || 0;

        if (model.occView) {
            const allBillItems = model.allBillItems ?? model.eventData.bill_item ?? [],
                expandableData = S25PricingUtil.formatExpandableFooterRows(allBillItems);
            data.push({
                id: lineItem.chargeToId,
                footerRow: true,
                row: [
                    ...(model.combineRelatedEvents ? [""] : []),
                    {
                        itemName: "Occurrence Line Item Amounts",
                        listItems: expandableData,
                        isExpandable: !!expandableData.length,
                        occLineItemAmounts: true,
                        charge_to_id: lineItem.chargeToId,
                    },
                    "",
                    S25PricingUtil.formatCurrency(occLineItemAdj),
                    "",
                    S25PricingUtil.formatCurrency(occLineItemTaxes),
                    occLineItemAdj + occLineItemTaxes,
                    "",
                ],
            });
        }
        const eventsArr = model.eventIds?.length ? model.eventIds : [model.eventId];
        eventsArr.forEach((eventId: any) => {
            const allBillItems =
                eventId === model.eventId
                    ? adjustmentsMap[lineItem.chargeToId]?.filter((adj: any) => adj.eventId === eventId)
                    : model.eventData.bill_item.filter((item: any) => item.eventId === eventId);

            const rowData = [
                ...(model.combineRelatedEvents ? [eventLocatorMap[eventId]] : []),
                `${model.occView ? "Event " : ""}Adjustments`,
                "",
                {
                    allBillItems: allBillItems?.length > 0 ? allBillItems : model.eventData.bill_item,
                    eventData: model.eventData,
                    eventIds: model.eventIds,
                    eventLocator: model.eventLocator,
                    canAdjust: canAdjust,
                    canEdit: canEdit,
                    canEditPricing: canEditPricing,
                    combineRelatedEvents: model.combineRelatedEvents,
                    evBillId: evBillId,
                    eventId: +eventId,
                    orgId: lineItem.chargeToId,
                },
                "",
                "",
                allBillItems?.length > 0 ? allBillItems : model.eventData.bill_item,
                ...Array(5).fill(""),
            ];

            data.push({ id: lineItem.chargeToId, row: rowData });
        });

        return data;
    }

    public static getSummaryData(set: Invoice, rowMap: InvoiceSummaryData, combinedEvents: boolean, eventId: number) {
        if (!set.billing?.subtotals?.[0]?.accountEvent) {
            const { includeEventType, billName, completed, startDt, endDt } = set.billDefn;
            rowMap.invoicedItems.invoiceNamesAndIds.push({
                name: billName,
                id: set.id,
                includeEventType: includeEventType,
                rsrvIds: new Set(),
                reqIds: new Set(),
                spaceIds: new Set(),
                resourceIds: new Set(),
                locked: completed ?? false,
                isDateRange: !!startDt && !!endDt,
                lineItemAdjustments: [],
                rsrvObjectIdMap: {},
            });
            return;
        }
        let accountData = set.billing.subtotals[0].accountEvent;
        if (!combinedEvents) {
            accountData = set.billing.subtotals[0].accountEvent.filter((account) => account.eventId === eventId);
            set.billing.lineItems = set.billing.lineItems.filter((item) => item.eventId === eventId);
            set.payments = set.payments.filter((payment) => payment.eventId === eventId);
        }
        // form subtotal data for each pricing set
        if (accountData) {
            accountData.reduce((map: InvoiceSummaryData["subtotals"], subtotal: InvoiceSummarySubtotal) => {
                const orgId = subtotal.chargeToId;
                const subtotalAccount = set.billing?.subtotals?.[0]?.account?.find(
                    (account) => account.chargeToId === orgId,
                );
                const outstandingBalance = subtotalAccount?.outstandingBalance ?? 0;
                const grossAdjustments = subtotalAccount?.grossAdjustments ?? 0;
                const grandTotal = subtotalAccount?.grandTotal ?? 0;
                if (!map[orgId]) {
                    map[orgId] = {
                        ...subtotal,
                        eventTypeListPrice: 0,
                        eventTypeAdjustments: 0,
                        eventTypeTotalCharge: 0,
                        occurrenceLineItems: [],
                        nonOccurrenceLineItems: [],
                        outstandingBalance,
                        grossAdjustments,
                        grandTotal,
                    };
                } else {
                    const notAddedAttrs = [
                        "chargeToId",
                        "eventId",
                        "occurrenceLineItems",
                        "nonOccurrenceLineItems",
                        "outstandingBalance",
                        "grossAdjustments",
                        "grandTotal",
                    ];
                    map[orgId].outstandingBalance += outstandingBalance;
                    map[orgId].grossAdjustments += grossAdjustments;
                    map[orgId].grandTotal += grandTotal;
                    for (let item in map[orgId]) {
                        if (!notAddedAttrs.includes(item)) (map[orgId] as any)[item] += (subtotal as any)[item] ?? 0;
                    }
                }
                return map;
            }, rowMap.subtotals);

            let rsrvIds: Set<number> = new Set(),
                reqIds: Set<number> = new Set(),
                spaceIds: Set<number> = new Set(),
                resourceIds: Set<number> = new Set(),
                lineItemAdj: LineItemAdjData[] = [],
                rsrvObjectMap: Record<number, { spaceIds: Set<number>; resourceIds: Set<number>; eventType: boolean }> =
                    {};
            // get totals of all line items for each pricing set
            set.billing.lineItems.reduce((map: { [key: string]: LineItem }, lineItem: LineItem) => {
                const hasOccurrences = !!lineItem.occurrences && lineItem.occurrences.length > 0;
                // unique identifier for each type of row (ie org, has occurrences and should display in occ totals or not, and itemId specific to the itemType) - also stores data to be extracted for occurrence sorting in sortOccurrenceItems
                const row = `${lineItem.itemId}-${lineItem.itemType}-${lineItem.chargeToId}-${hasOccurrences ? "occ" : "noOcc"}${combinedEvents ? "-" + lineItem.eventId : ""}`;
                const rateScheduleName = rowMap.rateSchedules?.find(
                    (rate: any) => rate.rateId === lineItem.rateId,
                )?.rateName;
                const rateGroupName = rowMap.rateGroups?.find(
                    (group: any) => group.rateGroupId === lineItem.rateGroup_id,
                )?.rateGroupName;

                // need all id values for payload to PricingService.putBillingCustom if assigning
                if (lineItem.itemType === lineItemType.REQUIREMENT) reqIds.add(lineItem.itemId);
                else if (lineItem.itemType === lineItemType.LOCATION) spaceIds.add(lineItem.itemId);
                else if (lineItem.itemType === lineItemType.RESOURCE) resourceIds.add(lineItem.itemId);
                else if (lineItem.itemType === lineItemType.EVENT) {
                    rowMap.invoicedItems.lineItems.add(`${lineItem.itemId}-${lineItem.itemType}`);
                    // get event type subtotals
                    rowMap.subtotals[lineItem.chargeToId].eventTypeListPrice += lineItem.listPrice ?? 0;
                    rowMap.subtotals[lineItem.chargeToId].eventTypeAdjustments =
                        (lineItem.adjustmentAmt ?? 0) + ((lineItem.adjustmentPercent / 100) * lineItem.listPrice || 0);
                    rowMap.subtotals[lineItem.chargeToId].eventTypeTotalCharge +=
                        (lineItem.total ?? 0) + (lineItem.tax ?? 0);
                }
                if (
                    (hasOccurrences || lineItem.itemType === lineItemType.REQUIREMENT) &&
                    (S25Util.isDefined(lineItem.adjustmentAmt) || S25Util.isDefined(lineItem.adjustmentPercent))
                ) {
                    lineItemAdj.push({
                        adjustmentAmt: lineItem.adjustmentAmt,
                        adjustmentPercent: lineItem.adjustmentPercent,
                        itemName: lineItem.itemName,
                        itemId: lineItem.itemId,
                        itemType: lineItem.itemType,
                        billId: set.id,
                        invoiceName: set.billDefn.billName,
                        chargeToId: lineItem.chargeToId,
                        profileId: lineItem.profileId,
                        adjustmentName: lineItem.adjustmentName,
                        rsrvIds: new Set(lineItem.occurrences?.map((occ: PricingOccurrence) => occ.rsrvId) ?? []),
                        newAdjustments: [],
                    });
                }
                lineItem.listPrice ??= 0;
                lineItem.adjustmentAmt =
                    (lineItem.adjustmentAmt ?? 0) + ((lineItem.adjustmentPercent / 100) * lineItem.listPrice || 0);
                lineItem.price ??= 0;
                lineItem.tax ??= 0;
                lineItem.total ??= 0;
                // line items with occurrence data are rendered within occurrence row expansion, those not associated with occurrences display as a separate row
                if (
                    !map[row] ||
                    (!map[row].occurrences?.length && lineItem.occurrences?.length) ||
                    (map[row].occurrences?.length && !lineItem.occurrences?.length)
                ) {
                    // for total data, just use adjustmentAmt to store the sum - no need to list each adjustment in summary
                    if (hasOccurrences) {
                        lineItem.occurrences.map((occ: any) => {
                            lineItem.itemType !== lineItemType.EVENT && rowMap.invoicedItems.occs.add(occ.rsrvId);
                            S25PricingUtil.addRsrvIds(occ.rsrvId, lineItem, rsrvIds, rsrvObjectMap);
                            occ.listPrice ??= 0;
                            occ.adjustmentAmt =
                                (occ.adjustmentAmt ?? 0) + ((occ.adjustmentPercent / 100) * occ.listPrice || 0);
                            return occ;
                        });
                    } else {
                        rowMap.invoicedItems.lineItems.add(`${lineItem.itemId}-${lineItem.itemType}`);
                    }
                    lineItem.tax ??= 0;
                    map[row] = { ...lineItem, rateGroupName, rateScheduleName };
                } else {
                    map[row] = {
                        ...map[row],
                        listPrice: (map[row].listPrice ?? 0) + (lineItem.listPrice ?? 0),
                        tax: map[row].tax + lineItem.tax,
                        price: map[row].price + lineItem.price,
                        total: map[row].total + lineItem.total,
                        adjustmentAmt:
                            (map[row].adjustmentAmt ?? 0) +
                            (lineItem.adjustmentAmt ?? ((lineItem.adjustmentPercent / 100) * lineItem.listPrice || 0)),
                    };
                    if (lineItem.taxes) {
                        lineItem.taxes.forEach((tax: any) => {
                            const taxMatch = map[row]?.taxes?.find((item: any) => item.taxId === tax.taxId);
                            if (taxMatch) {
                                taxMatch.taxCharge += tax.taxCharge ?? 0;
                            } else {
                                if (!map[row].taxes) map[row].taxes = [];
                                map[row].taxes.push(tax);
                            }
                        });
                    }
                    if (hasOccurrences) {
                        lineItem.occurrences.forEach((occ) => {
                            lineItem.itemType !== lineItemType.EVENT && rowMap.invoicedItems.occs.add(occ.rsrvId);
                            S25PricingUtil.addRsrvIds(occ.rsrvId, lineItem, rsrvIds, rsrvObjectMap);
                            const occMatch = map[row]?.occurrences?.find((item: any) => item.rsrvId === occ.rsrvId);
                            if (occMatch) {
                                occMatch.price += occ.price ?? 0;
                                occMatch.listPrice += (occ.listPrice ?? 0) || 0;
                                occMatch.total += occ.total ?? 0;
                                occMatch.adjustmentAmt =
                                    (occMatch.adjustmentAmt ?? 0) +
                                    (occ.adjustmentPercent
                                        ? (occ.adjustmentPercent / 100) * occ.listPrice
                                        : (occ.adjustmentAmt ?? 0));
                            } else {
                                if (!map[row].occurrences) map[row].occurrences = [];
                                occ.adjustmentAmt =
                                    (occ.adjustmentAmt ?? 0) +
                                    ((occ.adjustmentPercent / 100) * (occ.listPrice ?? 0) || 0);
                                map[row].occurrences.push(occ);
                            }
                        });
                    } else {
                        rowMap.invoicedItems.lineItems.add(`${lineItem.itemId}-${lineItem.itemType}`);
                    }
                }
                return map;
            }, rowMap.lineItems);

            const { includeEventType, billName, completed, startDt, endDt } = set.billDefn;
            rowMap.invoicedItems.invoiceNamesAndIds.push({
                name: billName,
                id: set.id,
                includeEventType: includeEventType,
                rsrvIds: rsrvIds,
                reqIds: reqIds,
                spaceIds: spaceIds,
                resourceIds: resourceIds,
                locked: completed ?? false,
                isDateRange: !!startDt && !!endDt,
                lineItemAdjustments: lineItemAdj,
                rsrvObjectIdMap: rsrvObjectMap,
            });
        }
    }

    public static addRsrvIds(
        rsrvId: number,
        lineItem: LineItem,
        rsrvIds: Set<number>,
        rsrvIdMap: Record<number, { spaceIds: Set<number>; resourceIds: Set<number>; eventType: boolean }>,
    ) {
        rsrvIds.add(rsrvId);
        if (!rsrvIdMap[rsrvId]) rsrvIdMap[rsrvId] = { spaceIds: new Set(), resourceIds: new Set(), eventType: null };
        if (lineItem.itemType === lineItemType.LOCATION) rsrvIdMap[rsrvId].spaceIds.add(lineItem.itemId);
        else if (lineItem.itemType === lineItemType.RESOURCE) rsrvIdMap[rsrvId].resourceIds.add(lineItem.itemId);
        else if (lineItem.itemType === lineItemType.EVENT) rsrvIdMap[rsrvId].eventType = true;
    }

    public static sortOccurrenceItems(invoiceData: any) {
        Object.entries(invoiceData.lineItems).forEach(([key, value]: any) => {
            // key stores data for orgId and whether the item is an occurrence or not - set in getSummaryData
            const [, , orgId, occVal] = key.split("-");

            if (occVal === "occ") {
                invoiceData.subtotals[orgId].occurrenceLineItems.push(value);
            } else {
                invoiceData.subtotals[orgId].nonOccurrenceLineItems.push(value);
            }
        });
    }

    private static getNonInvoicedAdj(allBillItems: LineItemI[]) {
        return allBillItems.reduce((sum: number, item: LineItemI | LineItem) => {
            let adjustment = 0;
            if (item.occurrences?.length) {
                // if non-invoiced, only get adjustment from aggregate of non-invoiced occurrences
                adjustment = item.occurrences.reduce(
                    (occSum: number, occ: LineItem) => occSum + (occ.adjustmentAmt ?? 0),
                    0,
                );
            } else {
                adjustment = (item.taxable_amt ?? 0) - (item.list_price ?? 0);
            }
            return sum + adjustment;
        }, 0);
    }

    private static getNonInvoicedListPrice(allBillItems: LineItemI[]) {
        return allBillItems.reduce((sum: number, item: LineItemI | LineItem) => {
            let listPrice = 0;
            if (item.occurrences?.length) {
                // if non-invoiced, only get list price from aggregate of non-invoiced occurrences
                listPrice = item.occurrences.reduce(
                    (occSum: number, occ: LineItem) => occSum + (occ.listPrice ?? 0),
                    0,
                );
            } else {
                listPrice = item.list_price ?? 0;
            }
            return sum + listPrice;
        }, 0);
    }

    private static getNonInvoicedPrice(allBillItems: LineItemI[]) {
        return allBillItems.reduce((sum: number, item: LineItemI | LineItem) => {
            let totalCharge = 0;
            if (item.occurrences?.length) {
                // if non-invoiced, only get total charge from aggregate of non-invoiced occurrences
                totalCharge = item.occurrences.reduce((occSum: number, occ: LineItem) => occSum + (occ.price ?? 0), 0);
            } else {
                totalCharge = item.taxable_amt ?? 0;
            }
            return sum + totalCharge;
        }, 0);
    }

    private static getNonInvoicedTotal(allBillItems: LineItemI[]) {
        return allBillItems.reduce((sum: number, item: LineItemI | LineItem) => {
            let totalCharge = 0;
            if (item.occurrences?.length) {
                // if non-invoiced, only get total charge from aggregate of non-invoiced occurrences
                totalCharge = item.occurrences.reduce((occSum: number, occ: LineItem) => occSum + (occ.total ?? 0), 0);
            } else {
                totalCharge = item.total_charge ?? 0;
            }
            return sum + totalCharge;
        }, 0);
    }

    private static getOccLineItemAdjustments(billingItems: LineItemI[] = []) {
        return (
            billingItems.reduce((sum: number, item: LineItemI) => {
                if (item.occurrences?.length && (item.adjustment_amt || item.adjustment_percent)) {
                    sum += (item.taxable_amt ?? 0) - (item.list_price ?? 0);
                }
                return sum;
            }, 0) || 0
        );
    }
}

export type OccDataMap = {
    [key: number]: Occurrence;
    dateFormat: string;
    timeFormat: string;
};

export type RsrvIdMap = { spaces: Set<number>; resources: Set<number> };
